import React, { useState } from 'react';
import { Trash2, MessageSquare, ListTodo, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { ROLE_GRAD, fmtTime, initials } from './slateUtils';
import { AttachmentView, RichContent } from './SlateAttachments';

// Simple reply item (non-recursive)
function ReplyItem({ msg, token, userId, onDelete }) {
  const grad = ROLE_GRAD[msg.user_role] || ROLE_GRAD.default;
  return (
    <div className="flex items-start gap-2 px-3 py-1.5 rounded-lg hover:bg-[#1a1a1a] group/rep transition-colors"
      data-testid={`slate-reply-${msg.id}`}>
      <div className={`h-6 w-6 rounded-md bg-gradient-to-br ${grad} flex items-center justify-center shrink-0 text-[10px] font-bold text-white mt-0.5`}>
        {initials(msg.user_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="text-xs font-semibold text-[#f0f0f0]">{msg.user_name}</span>
          <span className="text-[10px] text-[#444]">{fmtTime(msg.created_at)}</span>
        </div>
        <p className="text-xs text-[#d0d0d0] whitespace-pre-wrap break-words leading-relaxed">
          <RichContent content={msg.content} />
        </p>
        {(msg.attachments || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {msg.attachments.map((a, i) => <AttachmentView key={a.id || i} att={a} token={token} />)}
          </div>
        )}
      </div>
      {msg.user_id === userId && (
        <button onClick={() => onDelete(msg.id)}
          className="h-5 w-5 opacity-0 group-hover/rep:opacity-100 flex items-center justify-center text-[#555] hover:text-red-400 transition-all shrink-0">
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// Thread view component
export function ThreadView({ msgId, channelId, projectId, token, userId, replies, loading, onReply, onDeleteReply }) {
  return (
    <div className="mt-1 space-y-0.5 border-l-2 border-[#2a2a2a] ml-2 pl-2">
      {loading && <div className="text-xs text-[#555] px-2 py-1">Loading replies...</div>}
      {replies.map(r => (
        <ReplyItem key={r.id} msg={r} token={token} userId={userId} onDelete={onDeleteReply} />
      ))}
      <button onClick={onReply}
        className="text-xs text-[#555] hover:text-[#f0f0f0] flex items-center gap-1 px-2 py-1 transition-colors">
        <Plus className="h-3 w-3" /> Reply
      </button>
    </div>
  );
}

// Main message item
export function MessageItem({ msg, userId, token, onReply, onDelete, onCreateTask, isReply }) {
  const grad = ROLE_GRAD[msg.user_role] || ROLE_GRAD.default;
  const [showActions, setShowActions] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const loadReplies = async () => {
    if (loadingReplies || !token) return;
    setLoadingReplies(true);
    try {
      const { default: axios } = await import('axios');
      const res = await axios.get(
        `${API}/projects/${msg.project_id}/slate/channels/${msg.channel_id}/messages/${msg.id}/replies`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplies(res.data);
    } catch (err) { console.error('Load replies error:', err); } finally { setLoadingReplies(false); }
  };

  const toggleThread = async () => {
    if (!showThread && msg.reply_count > 0) await loadReplies();
    setShowThread(v => !v);
  };

  const handleDeleteReply = async (replyId) => {
    try {
      const { default: axios } = await import('axios');
      await axios.delete(
        `${API}/projects/${msg.project_id}/slate/channels/${msg.channel_id}/messages/${replyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplies(prev => prev.filter(r => r.id !== replyId));
    } catch (err) { console.error('Delete reply error:', err); }
  };

  return (
    <div
      className={`flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-[#1a1a1a] group/msg transition-colors ${isReply ? 'ml-10 bg-[#111] border-l-2 border-[#2a2a2a] pl-3' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      data-testid={`slate-message-${msg.id}`}
    >
      <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${grad} flex items-center justify-center shrink-0 text-xs font-bold text-white mt-0.5`}>
        {initials(msg.user_name)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap mb-0.5">
          <span className="text-sm font-semibold text-[#f0f0f0]">{msg.user_name}</span>
          {msg.user_dept && msg.user_dept !== 'ALL' && (
            <span className="text-[10px] text-[#555] uppercase">{msg.user_dept.replace('_', ' ')}</span>
          )}
          <span className="text-[10px] text-[#444]">{fmtTime(msg.created_at)}</span>
          {msg.edited && <span className="text-[9px] text-[#444] italic">(edited)</span>}
        </div>

        <p className="text-sm text-[#d0d0d0] whitespace-pre-wrap break-words leading-relaxed">
          <RichContent content={msg.content} />
        </p>

        {(msg.attachments || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {msg.attachments.map((a, i) => <AttachmentView key={a.id || i} att={a} token={token} />)}
          </div>
        )}

        {!isReply && (msg.reply_count > 0 || showThread) && (
          <button onClick={toggleThread}
            className="flex items-center gap-1.5 mt-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            data-testid={`thread-toggle-${msg.id}`}>
            <MessageSquare className="h-3 w-3" />
            {msg.reply_count} repl{msg.reply_count === 1 ? 'y' : 'ies'}
            {showThread ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}

        {showThread && !isReply && (
          <ThreadView
            msgId={msg.id}
            channelId={msg.channel_id}
            projectId={msg.project_id}
            token={token}
            userId={userId}
            replies={replies}
            loading={loadingReplies}
            onReply={() => onReply(msg)}
            onDeleteReply={handleDeleteReply}
          />
        )}
      </div>

      {showActions && !isReply && (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onReply(msg)} className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-blue-400 hover:bg-blue-400/10 transition-all" title="Reply">
            <MessageSquare className="h-3 w-3" />
          </button>
          <button onClick={() => onCreateTask(msg)} className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-[#e8c547] hover:bg-[#e8c547]/10 transition-all" title="Create task">
            <ListTodo className="h-3 w-3" />
          </button>
          {msg.user_id === userId && (
            <button onClick={() => onDelete(msg.id)} className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-red-400 hover:bg-red-400/10 transition-all" title="Delete">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
